/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DataGridModule
 */

import {
    Pipe,
    PipeTransform,
} from '@angular/core';

import { isNaN } from 'lodash';

import {
    ConvertService,
    IMetricsValue,
} from 'ng/modules/core/services/convert-service/convert.service';
import { Series } from 'ajs/modules/metrics/factories/series.factory';
import { MetricsDisplayValueType } from 'ng/root-store/user-preferences/user-preferences.state';

/**
 * @description
 *     Pipe to normalize Series data to a hash with a value and a unit.
 *     Used for display in metrics grid cell.
 * @author Zhiqian Liu
 */

@Pipe({
    name: 'normalizeMetricsCellData',
})
export class NormalizeMetricsCellDataPipe implements PipeTransform {
    constructor(private readonly convertService: ConvertService) {}

    /**
     * Transform series data to metrics value according to the rendering type.
     */
    public transform(
        series: Series,
        displayType: MetricsDisplayValueType,
        // dummy param for the caller to programatically trigger this pipe by simply flipping the
        // param value
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        flippingCoin: boolean,
    ): IMetricsValue {
        if (!series.hasData()) {
            return {
                value: '',
            };
        }

        if (displayType === MetricsDisplayValueType.CURRENT) {
            return this.convertService.getVal(
                series.getValue(displayType) as string | number,
                series.getUnits(),
            );
        } else if (displayType === MetricsDisplayValueType.SUM) {
            const seriesValue = series.getValue(displayType) as string | number;

            if (isNaN(Number(seriesValue))) {
                return {
                    value: 'NA',
                };
            } else {
                const metricsVal = this.convertService.getVal(
                    seriesValue, series.getUnits(),
                );

                metricsVal.unit = this.convertService.getUnitForSum(metricsVal.unit);

                // Convert Xb from bits to Bytes
                // TODO: move this into convert
                if (metricsVal.unit && metricsVal.unit.length === 2 &&
                    metricsVal.unit[1] === 'b') {
                    metricsVal.unit = `${metricsVal.unit[0]}bytes`;
                    (metricsVal.value as number) /= 8;
                }

                return metricsVal;
            }
        } else {
            return this.convertService.getVal(
                series.getValue(displayType) as string | number,
                series.getUnits(),
            );
        }
    }
}
